'use client';

import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { usersDelete, usersUpdateProfile } from '@/app/apis';
import { BaseLineClose } from '@/app/components/icons/BaseLineClose';
import { BaseLineExclamation } from '@/app/components/icons/BaseLineExclamation';
import { CustomTooltip } from '@/app/components/shared/CustomTooltip';
import { UserAvatar } from '@/app/components/shared/UserAvatar';
import { Button } from '@/app/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/app/components/ui/dialog';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { toast } from '@/app/components/ui/use-toast';
import { SITE } from '@/app/constants';
import { useFetch } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import { getByteLength } from '@/app/utils';
import { handleServiceError } from '@/app/utils/client';
import { getCurrentUser } from '../apis';
import { StreamEmergencyContactInput } from './StreamEmergencyContactInput';

export const StreamProfileEditDialog = ({ open, onClose }: { open: boolean; onClose: () => void }) => {
  const t = useTranslations();
  const { userInfo, updateUserInfo, isTeamSpace } = useStore((state) => ({
    userInfo: state.userInfo,
    updateUserInfo: state.updateUserInfo,
    isTeamSpace: state.isTeamSpace,
  }));
  const [nickname, setNickname] = useState('');
  const [biography, setBiography] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingNickname, setEditingNickname] = useState(false);
  const [editingBiography, setEditingBiography] = useState(false);
  const [confirmField, setConfirmField] = useState<'nickname' | 'biography' | null>(null);
  const [fieldError, setFieldError] = useState<'nickname' | 'biography' | null>(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const isNicknameLocked = userInfo?.auditing_info?.nickname === true;
  const isAvatarLocked = userInfo?.auditing_info?.avatar === true;
  const isBiographyLocked = userInfo?.auditing_info?.biography === true;

  useEffect(() => {
    if (!open || !userInfo) return;
    setNickname(userInfo.nickname || '');
    setBiography(userInfo.biography || '');
  }, [open, userInfo]);

  const { run: refreshUser } = useFetch(getCurrentUser, {
    manual: true,
    suppressUnauthorizedLoginDialog: true,
    onSuccess: ({ data }) => data && updateUserInfo(data),
  });

  const saveField = async (field: 'nickname' | 'biography') => {
    setSaving(true);
    const { data, error } = await usersUpdateProfile({
      body: {
        profile: field === 'nickname' ? { nickname: nickname.trim() } : { biography },
        mask: field,
      },
    });
    setSaving(false);
    if (error) {
      handleServiceError(error, t);
      return;
    }
    if (data) {
      await refreshUser();
      toast({
        type: 'success',
        message: t(field === 'nickname' ? 'personal.username.success' : 'personal.bio.success'),
      });
      setEditingNickname(false);
      setEditingBiography(false);
    }
  };

  const handleSaveField = (field: 'nickname' | 'biography') => {
    if (field === 'nickname' && (!nickname.trim() || getByteLength(nickname) < 4 || getByteLength(nickname) > 30)) {
      setFieldError(field);
      toast({
        type: 'error',
        message: !nickname.trim() ? t('personal.username.requiredError') : t('personal.username.lengthError'),
      });
      return;
    }
    if (field === 'biography' && getByteLength(biography) > 100) {
      setFieldError(field);
      toast({ type: 'error', message: t('personal.bio.lengthError') });
      return;
    }
    setFieldError(null);
    setConfirmField(field);
  };

  if (!userInfo) return null;

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => !nextOpen && onClose()}>
      <DialogContent
        className="flex max-h-[min(90vh,900px)] w-[640px] flex-col overflow-hidden p-0 text-system-white sm:w-[calc(100vw-32px)]"
        closable={false}
      >
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <div className="relative shrink-0 border-system-white12 border-b px-8 py-5 sm:px-6">
            <div className="font-bold text-2xl lg:text-xl">{t('personal.accountSetting')}</div>
            <BaseLineClose
              className="-translate-y-1/2 absolute top-1/2 right-8 h-6 w-6 cursor-pointer sm:right-6"
              onClick={onClose}
            />
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto px-8 py-6 sm:px-6">
            <div className="mb-6 font-medium text-xl lg:mb-4 lg:text-lg">{t('personal.basicInformation')}</div>
            <div className="mb-6">
              <Label className="mb-4 inline-block font-medium text-[18px] lg:mb-3 lg:text-sm">
                {t('personal.avatar')}
              </Label>
              <div className="relative block">
                <UserAvatar
                  userInfo={userInfo}
                  className="size-20 md:size-12"
                  editable={true}
                  editableDisabled={isAvatarLocked}
                />
              </div>
            </div>
            <div className="mb-6">
              <div className="mb-3 flex items-center justify-between">
                <Label className="font-medium text-base">{t('personal.username.label')}</Label>
                <div className="flex items-center gap-1">
                  <CustomTooltip
                    content={isNicknameLocked ? t('personal.bio.auditing') : undefined}
                    trigger="hover"
                    disableHoverableContent
                  >
                    <span>
                      <button
                        type="button"
                        disabled={isNicknameLocked}
                        className={isNicknameLocked ? 'cursor-not-allowed text-system-white48' : 'text-system-blue02'}
                        onClick={() => (editingNickname ? handleSaveField('nickname') : setEditingNickname(true))}
                      >
                        {editingNickname ? t('personal.save') : t('personal.edit')}
                      </button>
                    </span>
                  </CustomTooltip>
                  <CustomTooltip content={t('personal.username.info')} trigger="hover" disableHoverableContent>
                    <BaseLineExclamation className="text-base text-system-text05" />
                  </CustomTooltip>
                </div>
              </div>
              <Input
                value={nickname}
                disabled={!editingNickname || isNicknameLocked}
                onChange={(event) => setNickname(event.target.value)}
                maxLength={31}
                className={fieldError === 'nickname' ? 'border-system-error' : undefined}
              />
            </div>
            <div className="mb-6">
              <div className="mb-3 flex items-center justify-between">
                <Label className="font-medium text-base">{t('personal.bio.label')}</Label>
                <div className="flex items-center gap-1">
                  <CustomTooltip
                    content={isBiographyLocked ? t('personal.bio.auditing') : undefined}
                    trigger="hover"
                    disableHoverableContent
                  >
                    <span>
                      <button
                        type="button"
                        disabled={isBiographyLocked}
                        className={isBiographyLocked ? 'cursor-not-allowed text-system-white48' : 'text-system-blue02'}
                        onClick={() => (editingBiography ? handleSaveField('biography') : setEditingBiography(true))}
                      >
                        {editingBiography ? t('personal.save') : t('personal.edit')}
                      </button>
                    </span>
                  </CustomTooltip>
                  <CustomTooltip content={t('personal.bio.info')} trigger="hover" disableHoverableContent>
                    <BaseLineExclamation className="text-base text-system-text05" />
                  </CustomTooltip>
                </div>
              </div>
              <textarea
                value={biography}
                disabled={!editingBiography || isBiographyLocked}
                onChange={(event) => setBiography(event.target.value)}
                className={`flex min-h-[100px] w-full resize-none rounded-8 border border-system-white12 bg-system-bg04 px-3 py-2 text-sm text-white focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-60 ${fieldError === 'biography' ? 'border-system-error' : ''}`}
              />
            </div>
            {!isTeamSpace ? (
              <>
                {userInfo.email ? (
                  <div className="mb-6">
                    <Label className="mb-3 inline-block font-medium text-base">{t('personal.email.label')}</Label>
                    <Input value={userInfo.email} disabled />
                  </div>
                ) : null}
                {userInfo.phone ? (
                  <div className="mb-6">
                    <Label className="mb-3 inline-block font-medium text-base">{t('personal.phone.label')}</Label>
                    <Input value={userInfo.phone} disabled />
                  </div>
                ) : null}
              </>
            ) : null}
            {!isTeamSpace && SITE === 'china' ? (
              <>
                <div className="my-8 border-system-white12 border-t lg:my-6" />
                <StreamEmergencyContactInput />
              </>
            ) : null}
            <div className="mt-8 border-system-white12 border-t lg:mt-6" />
            <div className="mt-8 mb-6 font-medium text-xl lg:mt-6 lg:mb-4 lg:text-lg">{t('personal.delete.label')}</div>
            <Button onClick={() => setDeleteOpen(true)} variant="danger">
              {t('personal.delete.button')}
            </Button>
          </div>
        </div>
        <Dialog open={deleteOpen} onOpenChange={setDeleteOpen} modal={false}>
          <DialogContent closable={false}>
            <DialogHeader>
              <DialogTitle>{t('popup.dialog.deleteUser.title')}</DialogTitle>
              <DialogDescription>
                {t.rich('popup.dialog.deleteUser.description', {
                  bold: (chunks) => <span className="font-bold">{chunks}</span>,
                  br: () => <br />,
                })}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="outline"
                size="dialog"
                disabled={deleting}
                onClick={async () => {
                  setDeleting(true);
                  const { error } = await usersDelete({});
                  setDeleting(false);
                  if (error) return;
                  updateUserInfo(null);
                  onClose();
                  setDeleteOpen(false);
                }}
              >
                {t('popup.dialog.deleteUser.confirm')}
              </Button>
              <Button variant="primary" size="dialog" onClick={() => setDeleteOpen(false)}>
                {t('popup.dialog.deleteUser.cancel')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        <Dialog open={confirmField !== null} onOpenChange={(nextOpen) => !nextOpen && setConfirmField(null)}>
          <DialogContent className="w-[642px]" closable={false}>
            <div className="font-medium text-system-text01 text-xl">
              {confirmField === 'nickname' ? t('personal.username.dialogTitle') : t('personal.bio.dialogTitle')}
            </div>
            <div className="text-sm text-white">
              {confirmField === 'nickname'
                ? t('personal.username.dialogDescription')
                : t('personal.bio.dialogDescription')}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setConfirmField(null)} disabled={saving}>
                {t('create.createForm.upload.cancel')}
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  if (confirmField) void saveField(confirmField);
                  setConfirmField(null);
                }}
                loading={saving}
              >
                {t('create.createForm.upload.confirm')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DialogContent>
    </Dialog>
  );
};
